import { Inject, Injectable, Injector } from '@angular/core';
import { AuthService, ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { Actions, Effect, ofType } from '@ngrx/effects';
import _ from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, tap } from 'rxjs/operators';
import { SessionService } from '../../services/session.service';
import { PlatformType } from '../../types/muni-environment';
import { Session } from '../../types/session.types';
import {
  LoadSessionFailed,
  LoadSessionSuccess,
  SessionActionTypes
} from './actions';

@Injectable()
export class SessionEffects {
  private readonly platform: PlatformType;

  @Effect()
  public loadSession$: Observable<
    LoadSessionFailed | LoadSessionSuccess
  > = this.actions$.pipe(
    ofType(SessionActionTypes.Load),
    exhaustMap(() =>
      this.sessionService.getSession().pipe(
        map((session) => new LoadSessionSuccess(session)),
        catchError((error) =>
          of(new LoadSessionFailed(error && (error as Error).message))
        )
      )
    )
  );

  @Effect()
  public reLoadSession$: Observable<
    LoadSessionFailed | LoadSessionSuccess
  > = this.actions$.pipe(
    ofType(SessionActionTypes.ReLoad),
    exhaustMap(() =>
      this.sessionService.reLoadSession().pipe(
        map((response) => {
          const authorizationHeader = response.headers.get('Authorization');
          if (authorizationHeader) {
            const token = this.extractToken(authorizationHeader);
            localStorage.setItem('token', token);
          }
          return new LoadSessionSuccess(response.body);
        }),
        catchError((error) =>
          of(new LoadSessionFailed(error && (error as Error).message))
        )
      )
    )
  );

  @Effect({ dispatch: false })
  public loadSessionSuccess$: Observable<
    LoadSessionFailed | LoadSessionSuccess
  > = this.actions$.pipe(
    ofType(SessionActionTypes.LoadSuccess),
    filter(() => this.platform === PlatformType.polaris),
    tap((action) => {
      const auth = this.injector.get(AuthService);
      const userId = _.get(auth, 'currentUser.profile.sub');
      const session = action.payload as Session.SessionModel;

      if (_.isNil(session)) {
        return;
      }

      const pendo = window['pendo'];
      if (pendo) {
        const { deptId, deptType, firmId, persona } = session;
        pendo.updateOptions({
          visitor: {
            id: userId
          },
          account: {
            id: deptId,
            dept_id: deptId,
            dept_type: deptType,
            firm_id: firmId,
            persona: persona
          }
        });
      }
    })
  );

  private extractToken(authorizationHeader: string): string | null {
    const tokenPrefix = 'Bearer ';
    if (authorizationHeader.startsWith(tokenPrefix)) {
      return authorizationHeader.slice(tokenPrefix.length);
    }
    return null;
  }

  constructor(
    public sessionService: SessionService,
    private actions$: Actions,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig,
    private readonly injector: Injector
  ) {
    this.platform =
      (config.params.platform as PlatformType) || PlatformType.polaris;
  }
}
